import { Injectable } from '@angular/core';
import { ValidationError } from '@core-modules/components/core-forms/validation/validation-error';
import { DeviceClientType } from '@core-modules/providers/device/enums/device-client-type';
import { CoreHttpClient } from '@core-modules/providers/core-http-client/core-http-client';
import { DeviceProvider } from '@core-modules/providers/device/device';
import { EnvironmentProvider } from '@core-modules/providers/environment-variables/environment';
import { SessionStorageProvider } from '@core-modules/providers/session-storage/session-storage';
import { ConfigurationOption, CoreConfiguration } from 'app/providers/core-configuration';
import { ValidatePinResult } from 'authorization/models/validate-pin-result';
import { LoginOutcome } from '../../enums/login-outcome';
import { ClearPinAndLogoutResult } from '../../models/clear-pin-logout-result';
import { CreateAccountBeginResult } from '../../models/create-account-begin-result';
import { CreateAccountEndResult } from '../../models/create-account-end-result';
import { InviteCodeRequest } from '../../models/invite-code-request';
import { LoginBeginResult } from '../../models/login-begin-result';
import { LoginEndResult } from '../../models/login-end-result';

/*
  Generated class for the Authentication provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class AuthProvider
{

    //#region Constructors

    /**
     * Class constructor
     * @param http 
     * @param device 
     * @param environment 
     * @param coreConfig 
     * @param storage 
     */
    constructor
        (
            private http: CoreHttpClient,
            private device: DeviceProvider,
            private environment: EnvironmentProvider,
            private coreConfig: CoreConfiguration,
            private storage: SessionStorageProvider
        )
    {

    }

    //#endregion


    //#region Properties


    //#endregion


    //#region Methods - Sign In By Azure B2C

    /**
     * Perform a call to the LoginBegin endpoint to initiate the signing in process
     * @param azureB2CProvider 
     */
    public async logInBegin(): Promise<LoginBeginResult>
    {
        // get the time zone
        let timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

        await this.clearUserAuthenticationData();

        // Get the version number and device identifier
        let versionNumber = this.environment.appVersion;
        let deviceIdentifier = await this.device.getDeviceIdentifier();

        // Prepare the request object
        let requestObject = {
            timeZoneOffset: timeZone,
            deviceClientType: this.device.getDeviceClientType(),
            deviceGuid: deviceIdentifier,
            appVersion: versionNumber
        };

        return this.http.post<LoginBeginResult>(`${this.environment.apiEndpoint}/mobileapp/Account/AzureB2CLoginBegin`, requestObject);
    }

    /**
     * Perform a call to the LoginEnd endpoint to finalise the signing in process
     * @param url url to call to finalise the process
     */
    public async logInEnd(url: string): Promise<LoginEndResult>
    {
        let result = await this.http.get<LoginEndResult>(url);
        return this.processLoginEndResult(result);
    }

    //#endregion


    //#region Methods - Sign In By Pin & Biometrics

    /**
     * Interact with the API and log into the application using the pin
     * @param pin 
     */
    public loginByPinCode(pin: string): Promise<LoginEndResult>
    {
        // get the time zone
        let timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (this.device.isApplication())
        {
            // if platform is a device then perform the version
            return this.loginByPinCode_Device(pin, timeZone);
        } else
        {
            // if platform is a browser perform the version of the call
            // for the browser
            return this.loginByPinCode_Browser(pin, timeZone);
        }
    }

    /**
     * Interact with the API and log into the application using the pin
     * @param pin PIN code
     * @param timeZone time zone
     */
    private async loginByPinCode_Browser(pin: string, timeZone: string): Promise<LoginEndResult>
    {
        let loginToken = (await this.coreConfig.getApplicationState()).LoginToken;

        let requestObject = {
            timeZone: timeZone,
            deviceClientType: DeviceClientType.Web,
            pin: pin,
            loginToken: loginToken,
            appVersion: this.environment.appVersion
        };

        // perform the call
        let result = await this.http.post<LoginEndResult>(`${this.environment.apiEndpoint}/mobileapp/Account/PinLogin`, requestObject);
        return this.processLoginEndResult(result);
    }

    /**
     * Interact with the API and log into the application using the pin
     * @param pin PIN code
     * @param timeZone time zone
     */
    private async loginByPinCode_Device(pin: string, timeZone: string): Promise<LoginEndResult>
    {
        // clear the cookies
        this.http.clearCookies();

        // get the login token
        let loginToken = (await this.coreConfig.getApplicationState()).LoginToken;

        let requestObject = {
            timezoneOffset: timeZone,
            deviceClientType: this.device.getDeviceClientType(),
            pin: pin,
            deviceId: await this.device.getDeviceIdentifier(),
            loginToken: loginToken,
            appVersion: this.environment.appVersion
        };

        let result = await this.http.post<LoginEndResult>(`${this.environment.apiEndpoint}/mobileapp/Account/PinLogin`, requestObject);
        return this.processLoginEndResult(result);
    }

    /**
     * Interact with the API and log into the application using the pin
     */
    public async loginByFinerprint(): Promise<LoginEndResult>
    {
        // clear the cookies
        this.http.clearCookies();

        // get the timezone offset
        let timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // get the login token
        let loginToken = (await this.coreConfig.getApplicationState()).LoginToken;

        let requestObject = {
            timezoneOffset: timeZone,
            deviceClientType: this.device.getDeviceClientType(),
            deviceId: await this.device.getDeviceIdentifier(),
            loginToken: loginToken,
            appVersion: this.environment.appVersion
        };

        let result = await this.http.post<LoginEndResult>(`${this.environment.apiEndpoint}/mobileapp/Account/BiometricLogin`, requestObject)
        return this.processLoginEndResult(result);
    }

    /**
     * Process a login end result
     * @param result login end instance
     * @param resolve promise resolve
     * @param reject promise reject
     */
    private async processLoginEndResult(result: LoginEndResult): Promise<LoginEndResult>
    {
        // login was successful, proceed accordingly
        if (LoginOutcome[result.LoginOutcome] === LoginOutcome.Success)
        {
            // save the user identity
            await this.saveUserIdentity({
                sessionToken: result.SessionToken,
                applicationPermissions: result.ApplicationPermissions,
                isDemoAccount: result.IsDemoAccount
            });

            // retrieve the state
            let state = await this.coreConfig.getApplicationState();

            // update the properties of the state
            state.LoginToken = result.LoginToken;
            state.LoginDate = new Date();
            state.BiometricsEnabled = result.HasBiometricLogin;
            state.DeviceIdentifier = await this.device.getDeviceIdentifier();

            // save the application state
            await this.coreConfig.saveApplicationState(state);
        }
        else if (LoginOutcome[result.LoginOutcome] == LoginOutcome.CredentialsMismatch)
        {
            // Login was not successful, invalid credentials. Do nothing.
        }
        else if (
            LoginOutcome[result.LoginOutcome] == LoginOutcome.AccountDoesNotExist ||
            LoginOutcome[result.LoginOutcome] == LoginOutcome.AccountInvalid ||
            LoginOutcome[result.LoginOutcome] == LoginOutcome.InvalidLoginMethod ||
            LoginOutcome[result.LoginOutcome] == LoginOutcome.InvalidToken
        )
        {
            // Login was not successful, account compromised. Clear all.
            await this.clearUserAuthenticationData();
        }

        return result;
    }

    //#endregion


    //#region Methods - Sign Out

    /**
     * Communicate wit the Logout endpoint to logout the user
     */
    public async signOut(): Promise<any>
    {
        // perform the request
        await this.http.post(this.environment.apiEndpoint + '/mobileapp/Account/Logout', {});

        // clear the user's authentication data
        await this.clearUserAuthenticationData();
    }

    //#endregion 


    //#region Methods - Delete account

    /**
     * Communicate wit the Logout endpoint to logout the user
     */
    public async closeAccount(): Promise<any>
    {
        // retrieve the user's sessionToken
        let sessionToken = (await this.getUserIdentity()).sessionToken;

        // perform the request
        await this.http.post(this.environment.apiEndpoint + '/mobileapp/Account/CloseAccount', { sessionToken: sessionToken });

        // clear the user's authentication data
        await this.clearUserAuthenticationData();
    }

    //#endregion


    //#region Methods - Create Account

    /**
      * Call the sign up begin endpoint in the CoreAPI
      * @param inviteCode invite code provided by the user
      * @param provider provider with which the user will sign in
      */
    public createAccountBegin(inviteCode: string): Promise<CreateAccountBeginResult>
    {
        // Get the device client type
        let appVersion = this.environment.appVersion;

        // prepare the request object
        let requestObject = {
            inviteCode: inviteCode,
            deviceClientType: this.device.getDeviceClientType(),
            appVersion: appVersion
        };

        return this.http.post<CreateAccountBeginResult>(`${this.environment.apiEndpoint}/mobileapp/Account/CreateAccountBegin`, requestObject)
    }

    /**
     * Call the create account end endpoint in the Core API
     * @param signUpCoreEndUrl
     */
    public createAccountEnd(signUpCoreEndUrl: string): Promise<CreateAccountEndResult>
    {
        return this.http.get<CreateAccountEndResult>(signUpCoreEndUrl);
    }

    /**
     * Post the invite code request to the api endpoint
     * @param requestDetails
     */
    public requestInviteCode(requestDetails: InviteCodeRequest): Promise<ValidationError[]>
    {
        // post the request details to the api endpoint
        return this.http.post<ValidationError[]>(`${this.environment.apiEndpoint}/mobileapp/Account/RequestInviteCode`, requestDetails);
    }


    //#endregion


    //#region Methods - Utilities

    /**
     * Get the user's identity/session from local storage
     */
    public async getUserIdentity(): Promise<UserIdentity>
    {
        let value = await this.storage.getValue(ConfigurationOption.CORE_APPLICATION_STATE)
        if (value)
        {
            return JSON.parse(value);
        }
        else 
        {
            return null;
        }
    }

    /**
    * Save user's identity/session
    */
    public async saveUserIdentity(userIdentity: UserIdentity): Promise<void>
    {
        await this.storage.setValue(ConfigurationOption.CORE_APPLICATION_STATE, JSON.stringify(userIdentity));
    }

    //#endregion


    //#region Methods - Permissions

    /**
     * Check whether the user has permission to change application settings
     */
    public async canChangeApplicationSettings(): Promise<boolean>
    {
        let userIdentity = await this.getUserIdentity();

        if (userIdentity.applicationPermissions.find(x => x === SettingsPermission.UPDATE_ACCESS))
        {
            return Promise.resolve(true);
        } else
        {
            return Promise.resolve(false);
        }
    }

    //#endregion


    //#region Methods - PIN Code

    /**
    * Validate the pin code against the API
    * @param pinCode 
    */
    public async validatePinCode(pinCode: string): Promise<ValidatePinResult>
    {
        // prepare the reqest object
        let requestObject = {
            pin: pinCode
        };

        // perform the request
        return this.http.post<ValidatePinResult>(`${this.environment.apiEndpoint}/mobileapp/Account/ValidatePin`, requestObject);
    }

    /**
     * Reset the pin code
     */
    public async clearPinAndLogout(): Promise<ClearPinAndLogoutResult>
    {
        let loginToken = (await this.coreConfig.getApplicationState()).LoginToken;

        let requestObject = {
            loginToken: loginToken
        };

        this.http.clearCookies();

        // perform the request
        let result = await this.http.post<ClearPinAndLogoutResult>(`${this.environment.apiEndpoint}/mobileapp/Account/ClearPinAndLogout`, requestObject);

        // check whether there are any errors
        if (result.PinCodeChanged)
        {
            // reset all the user's data
            await this.clearUserAuthenticationData();
        }

        return Promise.resolve(result);
    }

    /**
     * Reset the user's authentication data
     */
    private async clearUserAuthenticationData()
    {
        // reset the user's configuration
        await this.coreConfig.clearConfiguration();

        // clear the local session storage
        this.storage.clearSessionStorage();

        // reset the device's HTTP layer cookies
        this.http.clearCookies();
    }

    //#endregion
}

/**
 * Enumeration for session indexes used
 */
export enum ClientSessionProperty
{
    CLIENT_ID = "core::sessionToken",
    IS_DEMO_ACCOUNT = "core::isDemoAccount",
    APPLICATION_PERMISSIONS = "core::applicationPermissions"
}

/**
 * Types of permissions
 */
enum SettingsPermission
{
    APP_ACCESS = "Mobile App :: Can Access Mobile App",
    UPDATE_ACCESS = "Mobile App :: Can Update User Settings"
}

export interface UserIdentity
{
    sessionToken: string;
    isDemoAccount: boolean;
    applicationPermissions: string[];
}
